/**
 * @fileoverview Public support page (/support), the Support URL in App Store
 * Connect.
 *
 * App Review opens it from the store listing, so it has to answer the three
 * things a reviewer and a customer look for without signing in: how to reach
 * a person, how to delete an account, and who bills a plan (Apple for the
 * iPhone app, Stripe for the website). /contact is a partnership page and is
 * not a support page.
 *
 * Opened from the iOS app (`?client=ios`) it drops the web billing answer and
 * the pricing links, like the guides (see lib/guideClient).
 */

import { useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Copy, Check, Mail } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import { Button, ButtonLink } from "@/components/studio/primitives";
import { privacyEmail, supportEmail } from "@/lib/companyBrand";
import { IOS_CLIENT, clientFromSearch, linkForClient } from "@/lib/guideClient";

const linkClass = "text-[var(--accent-yellow-text)] underline-offset-4 hover:underline";

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-8 border-b border-[var(--studio-divider)] py-8 last:border-0">
    <h2 className="mb-4 font-serif text-lg font-medium text-[var(--text-primary)]">{title}</h2>
    <div className="flex flex-col gap-3 text-sm leading-7 text-[var(--text-secondary)]">{children}</div>
  </section>
);

const Answer = ({ label, children }) => (
  <div className="rounded-xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] px-4 py-3">
    <p className="font-medium text-[var(--text-primary)]">{label}</p>
    <p className="text-[var(--text-secondary)]">{children}</p>
  </div>
);

/** Copy the address, falling back to selecting it where the clipboard is blocked. */
function useCopyAddress(address, textRef) {
  const [status, setStatus] = useState(null); // null | "copied" | "selected"
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setStatus("copied");
    } catch {
      const node = textRef.current;
      const selection = typeof window !== "undefined" ? window.getSelection?.() : null;
      if (node && selection) {
        const range = document.createRange();
        range.selectNodeContents(node);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      setStatus("selected");
    }
  };
  return { status, copy };
}

const SupportPage = () => {
  const client = clientFromSearch(useLocation().search);
  const ios = client === IOS_CLIENT;
  const address = supportEmail();
  const addressRef = useRef(null);
  const { status, copy } = useCopyAddress(address, addressRef);

  return (
    <PublicLayout>
      <main
        className="relative z-10 mx-auto max-w-2xl px-4 pb-20 pt-12 safe-area-padding md:px-8 md:pb-32 md:pt-20"
        data-testid="support-page"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="mb-10">
            <p className="mb-4 font-mono text-xs uppercase tracking-widest text-[var(--text-tertiary)]">Help</p>
            <h1 className="mb-5 font-serif text-3xl font-light tracking-tight text-[var(--text-primary)] md:text-4xl">
              Hans support
            </h1>
            <p className="text-sm leading-7 text-[var(--text-secondary)]">
              Questions about your account, billing or the app? Email us. A person reads every message.
            </p>
          </div>

          <div className="rounded-[20px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-6 md:p-8">
            <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--text-tertiary)]">
              Email support
            </p>
            <p
              ref={addressRef}
              className="mb-5 select-all break-all font-mono text-lg text-[var(--text-primary)] md:text-xl"
              data-testid="support-email"
            >
              {address}
            </p>
            <div className="flex flex-wrap gap-3">
              <Button variant="primary" type="button" onClick={copy} data-testid="support-copy">
                {status === "copied" ? (
                  <Check className="h-4 w-4" strokeWidth={2} aria-hidden />
                ) : (
                  <Copy className="h-4 w-4" strokeWidth={2} aria-hidden />
                )}
                {status === "copied" ? "Copied" : "Copy address"}
              </Button>
              <ButtonLink href={`mailto:${address}`} variant="ghost" data-testid="support-mailto">
                <Mail className="h-4 w-4" strokeWidth={2} aria-hidden />
                Write an email
              </ButtonLink>
            </div>
            <p className="mt-3 min-h-5 text-xs text-[var(--text-tertiary)]" role="status" aria-live="polite">
              {status === "copied" && "The address is on your clipboard."}
              {status === "selected" && "The address is selected. Copy it with your keyboard."}
            </p>
            <p className="mt-2 text-sm leading-7 text-[var(--text-secondary)]">
              Tell us the email on your account, whether you use the iPhone app or the website, and what
              happened. Please don't email lesson recordings or student details.
            </p>
          </div>

          <Section id="delete-account" title="Delete your account">
            <Answer label="Instructors">
              In the iPhone app, open Settings and tap Delete account at the bottom of the screen.
            </Answer>
            <Answer label="Students">
              Open Profile, in the iPhone app or in the student portal on the web, and tap Delete account.
            </Answer>
            <p>
              Deleting removes your account and its lesson data for good. It can't be undone. If you can't sign
              in, or you teach only on the website, email us from the address on the account and we will delete
              it for you.
            </p>
            <p>
              Deleting your account doesn't cancel an App Store subscription. Cancel it first in the Settings app
              on your iPhone: tap your name, then Subscriptions.
            </p>
            {!ios && (
              <p data-testid="support-web-cancel">
                A subscription you pay for on the Hans website is cancelled when you delete your account.
              </p>
            )}
          </Section>

          <Section id="billing" title="Billing through Apple or the web">
            <p>Where you started a plan decides who bills you and where you manage it.</p>
            <Answer label="Bought in the iPhone app">
              Apple bills you. Manage or cancel the subscription in the Settings app on your iPhone: tap your
              name, then Subscriptions. Apple handles refunds at{" "}
              <a href="https://reportaproblem.apple.com" className={linkClass} rel="noopener noreferrer">
                reportaproblem.apple.com
              </a>
              . On a new iPhone, tap Restore purchases in Settings in the app.
            </Answer>
            {!ios && (
              <div data-testid="support-web-billing">
                <Answer label="Bought on the Hans website">
                  Stripe bills you. Manage or cancel the plan on the website, in Settings under Plan &amp; billing.
                </Answer>
              </div>
            )}
            <Answer label="Covered by an organization">
              The organization pays for your account. Ask its administrator about your plan.
            </Answer>
          </Section>

          <Section id="contact" title="Contact us">
            <p>
              Support:{" "}
              <a href={`mailto:${address}`} className={linkClass}>
                {address}
              </a>
            </p>
            <p>
              Privacy requests, such as a copy of your data:{" "}
              <a href={`mailto:${privacyEmail()}`} className={linkClass}>
                {privacyEmail()}
              </a>
            </p>
            <p>
              Found a security problem? Read our{" "}
              <Link to={linkForClient("/security", client)} className={linkClass}>
                Security page
              </Link>
              .
            </p>
            <p>
              Step-by-step help is in the{" "}
              <Link to={linkForClient("/guide", client)} className={linkClass}>
                instructor guide
              </Link>{" "}
              and the{" "}
              <Link to={linkForClient("/student-guide", client)} className={linkClass}>
                student and parent guide
              </Link>
              .
            </p>
          </Section>
        </motion.div>
      </main>
    </PublicLayout>
  );
};

export default SupportPage;
